<script setup>

import {onMounted, ref, nextTick, watch, computed} from "vue";
import PackageForm from "./package-form.vue";
import {useModel} from "@/lib/useModel";
import helpers from "@/lib/helpers";
import PackageDetails from "./package-details.vue";
import {breakpointsBootstrapV5, useBreakpoints} from "@vueuse/core/index";
import ShipQuoteResults from "@/components/package/ship-quote-results.vue";
import {until} from '@vueuse/core'
import {refreshServicesFor} from "@/lib/freight";
import store from "@/store";

const props = defineProps({
  url: [String, Object],
  refresh: Function,
  perm: String,
  // the new-package kinds this record offers: one letter each (O outbound, R return / inbound,
  // L prepaid / customer label, M manual). One: a plain New Shipment button. More: a menu of just those
  noHelper: String,
  // what the menu calls each kind here, e.g. a PO's R is the vendor shipping to us
  modeLabels: {type: Object, default: () => ({})},
})

const MODE_LABELS = {O: 'Outbound Shipment', R: 'Return Label', L: 'Prepaid/Customer Label'}
const modeLabel = m => props.modeLabels[m] || MODE_LABELS[m] || m


const emits = defineEmits(['submit', 'copy'])

const breakpoints = useBreakpoints(breakpointsBootstrapV5)
const is_small = breakpoints.between('xs', 'lg')
const {model: shippable, refresh} = useModel(['po', 'order', 'quote'], props.url)
const selected = ref()
const duplicate = ref()
const newmode = ref()
const pkg_form = ref()

const quote = ref(false)
// the quote was asked for to book (Save, Quote, & Book): the line picked is booked straight away
const bookAfterPick = ref(false)

// a new package is being started (so not the chooser), as opposed to nothing selected yet
const starting = ref(false)

/** the New Shipment menu's choices, also listed in place of the form while there are no packages */
const startOptions = computed(() => {
  if (props.noHelper === undefined)
    return [{mode: undefined, title: 'Manual Shipment'}, ...['O', 'R', 'L'].map(m => ({mode: m, title: modeLabel(m)}))]
  if (props.noHelper.length <= 1)
    return [{mode: props.noHelper === 'M' ? undefined : props.noHelper, title: 'New Shipment'}]
  // M: a manual package (service and tracking entered by hand)
  return props.noHelper.split('').map(m => m === 'M' ? {mode: undefined, title: 'Manual Shipment'} : {mode: m, title: modeLabel(m)})
})
const noPackages = computed(() => !(shippable.value && shippable.value.packages && shippable.value.packages.length))

const update = async (replace) => {
  newmode.value = null
  duplicate.value = null
  if (replace === null)
    starting.value = false
  if (replace || replace === null)
    selected.value = replace
  else
    selected.value = await helpers.asyncAction(selected.value._links.self_url)

  refresh()
}

onMounted(() => {
  // the ship services as they are now: a list loaded before a change (a new trucking line, which
  // services quote first) would leave the picker blank or ship a freight placeholder unquoted
  store.dispatch('shipping_refresh')

  if (!is_small.value)
    selected.value = shippable.value.packages && shippable.value.packages.length && shippable.value.packages[0]
})

const copy = (mode) => {
  duplicate.value = ['fkshipping', 'pkg_length', 'pkg_width', 'pkg_height', 'pkg_weight'].reduce((obj, key) => {
    if (key in selected.value) {
      obj[key] = selected.value[key];
    }
    return obj;
  }, {});

  duplicate.value.group_packages = selected.value.group_packages && selected.value.group_packages.map(p => {
    return ['pkg_length', 'pkg_width', 'pkg_height', 'pkg_weight'].reduce((obj, key) => {
      if (key in p) {
        obj[key] = p[key];
      }
      return obj;
    }, {});

  })

  starting.value = true
  selected.value = null
  nextTick(() => newmode.value = mode)
}

const newpkg = (mode) => {
  starting.value = true
  selected.value = null
  nextTick(() => newmode.value = mode)
}

const onupdate = (n) => {
  selected.value = n
  refresh()
}

const bookQuestion = (offer, rate) =>
    `Book ${offer.carrier_name}${offer.service_name ? ' - ' + offer.service_name : ''} for $${Number(String(rate).replace(/,/g, '')).toFixed(2)}?`
    + ' This schedules the pickup with the carrier.'

const run_quote = (opts) => {
  bookAfterPick.value = !!(opts && opts.book)
  helpers.asyncAction(selected.value._links.quote_url).then(async r => {
    await refreshServicesFor((r.results || []).map(q => q.ship_id))
    quote.value = r
  })
}

const set_shipping = async (ship) => {
  if (selected.value.shipped)
    return

  quote.value = null

  await until(pkg_form).toBeTruthy()
  pkg_form.value.choose_quote(ship)

  if (bookAfterPick.value && ship.freight_offer) {
    if (!await helpers.confirm(bookQuestion(ship.freight_offer, ship.rate)))
      return
    bookAfterPick.value = false
    await pkg_form.value.book(true)
    return
  }

  // on a sales quote the freight estimate is the quote's shipping charge
  if (ship.freight_offer && shippable.value._links.package_url.href.startsWith('/api/quote/')
      && shippable.value.is_editable && helpers.can('quotes.write')) {
    await helpers.asyncAction(shippable.value._links.self_url.href, 'PUT', {
      data: {fkshipping: ship.ship_id, payment_shipping: ship.rate}
    })
    helpers.toast(`Quote shipping set to ${ship.freight_offer.carrier_name} ${Number(ship.rate).toFixed(2)}`)
    refresh()
  }

  document.getElementById('vue-modal-body').scrollTop = 0
}

watch(selected, () => newmode.value = null)

</script>

<template>
  <modal title="Shipments" large height="400px" no-scroll>
    <!-- while quote results show, the footer is theirs: back to the shipment without picking.
         Otherwise the form or the details own the footer, and a second portal here would hide their buttons -->
    <modal-portal to="footer" v-if="quote">
      <div class="d-flex justify-content-end">
        <b-button size="sm" class="px-4" variant="outline-secondary" @click="quote = undefined; bookAfterPick = false">Cancel</b-button>
      </div>
    </modal-portal>
    <template #buttons>
      <b-button v-if="quote" variant="link" class="text-white" size="sm"
                @click="quote = undefined; bookAfterPick = false">BACK
      </b-button>
      <b-button v-else-if="is_small && (selected !== undefined)" variant="link" class="text-white" size="sm"
                @click="selected = undefined">BACK
      </b-button>
    </template>
    <b-row class="mr-1">
      <b-col :cols="is_small ? 12 : 4" class="package-height overflow-auto" v-if="!is_small || selected === undefined">
        <!-- with no packages the same choices are listed beside it (wide screens): no button then -->
        <template v-if="!noPackages || is_small || starting">
          <b-btn size="sm" block variant="outline-primary" class="mb-2" v-if="startOptions.length === 1"
                 @click="newpkg(startOptions[0].mode)">New Shipment
          </b-btn>
          <b-dropdown v-else block size="sm" variant="outline-primary" text="New Shipment" class="mb-2"
                      :disabled="!!quote">
            <b-dropdown-item v-for="o in startOptions" :key="'m' + o.mode" @click="newpkg(o.mode)">{{ o.title }}</b-dropdown-item>
          </b-dropdown>
        </template>

        <b-list-group v-if="shippable.packages">
          <b-list-group-item :active="selected && (selected.id === pkg.id)" button v-for="pkg in shippable.packages"
                             class="d-flex"
                             :key="pkg.id" :disabled="!!quote" @click="selected = pkg">
            <div class="flex-fill">
              <div>{{ pkg.gli }}</div>
              <div>{{ pkg.shipping && pkg.shipping.carrier && pkg.shipping.carrier.description }}</div>
              <div>{{ pkg.shipping && pkg.shipping.description }}</div>
            </div>
            <div class="d-flex flex-column">
              <font-awesome-icon class="mb-2" :icon="['fas', 'rotate-left']" v-if="pkg.return_shipment"/>

              <font-awesome-icon :icon="['fas', 'cubes']" v-if="pkg.group_packages.length" v-b-tooltip.hover
                                 :title="pkg.group_packages.length + ' packages in group'"/>
              <font-awesome-icon :icon="['fas', 'cube']" v-else/>

              <font-awesome-icon class="mt-2 text-warning" v-if="pkg.voided" :icon="['fas', 'ban']"/>
              <font-awesome-icon v-else-if="pkg.shipped" class="mt-2" :icon="['fas', 'truck-fast']"/>
              <font-awesome-icon v-else-if="pkg.last_ship_error" :icon="['fas', 'triangle-exclamation']"
                                 class="mt-2 text-danger"/>
            </div>
          </b-list-group-item>
        </b-list-group>
      </b-col>
      <b-col :cols="is_small ? 12 : 8" class="package-height overflow-auto" id="ship_scroll"
             v-if="!is_small || selected !== undefined">
        <div class="mr-1">
          <component :is="'transition'" name="fade" mode="out-in">

            <div v-if="quote">
              <b-alert show variant="info" class="small" v-if="bookAfterPick">Pick a carrier to book it.</b-alert>
              <ship-quote-results :results="quote" @click="set_shipping"/>
            </div>

            <!-- nothing yet: what can be started, rather than an empty form -->
            <div v-else-if="!selected && !starting" key="start" class="d-flex flex-column justify-content-center start-choices">
              <h6 class="mb-2">What would you like to do?</h6>
              <b-list-group>
                <b-list-group-item button v-for="o in startOptions" :key="'m' + o.mode" @click="newpkg(o.mode)">
                  {{ o.title }}
                </b-list-group-item>
              </b-list-group>
            </div>

            <package-details :no-transition="is_small" :shippable="shippable" :value="selected" :perm="perm"
                             @submit="emits('submit')"
                             :key="selected && selected.id"
                             @copy="copy"
                             @quote="run_quote"
                             @input="(v) => update(v)"
                             v-else-if="selected && selected.shipped"/>

            <package-form :no-transition="is_small" :shippable="shippable" :value="selected" v-else :perm="perm"
                          :newmode="newmode"
                          :key="selected && selected.id"
                          ref="pkg_form"
                          :duplicate="duplicate"
                          @copy="copy"
                          @quote="run_quote"
                          @submit="$emit('submit')"
                          @input="onupdate"/>
          </component>
        </div>
      </b-col>
    </b-row>
  </modal>

</template>

<style scoped>
.package-height {
  height: 400px
}

/* the choices in the middle of the empty pane (the column is 400px) */
.start-choices {
  min-height: 390px;
}
</style>